import dayjs, { type Dayjs } from 'dayjs';
import { r, s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { DatePicker, Popover, Text } from '@/ui/primitives';
import {
  tableDatePickerOpenButtonSx,
  tableDatePickerTextFieldSx,
  tableDateQuickRangeTextSx,
  resolveDatePreview,
} from './tableToolbar.helpers';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';

const popoverFontSize = '15px';

type TableDatePopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  dateQuickFilter: DateQuickFilter;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  previewFromDate?: Dayjs | null;
  previewToDate?: Dayjs | null;
  onFromDateChange: (next: Dayjs | null) => void;
  onToDateChange: (next: Dayjs | null) => void;
};

export const TableDatePopover = ({
  open,
  anchorEl,
  onClose,
  dateQuickFilter,
  fromDate,
  toDate,
  previewFromDate = null,
  previewToDate = null,
  onFromDateChange,
  onToDateChange,
}: TableDatePopoverProps): JSX.Element => {
  const { displayedFromDate, displayedToDate, isQuickRangePreview } = resolveDatePreview(
    dateQuickFilter,
    fromDate,
    toDate,
    previewFromDate,
    previewToDate,
  );
  const shouldPreservePreviewFromDate =
    fromDate === null && toDate === null && displayedFromDate !== null && displayedToDate !== null;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      paperSx={{
        backgroundColor: 'var(--surface-page)',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${r('md')} !important`,
        boxShadow: 'none',
        overflow: 'hidden !important',
        minWidth: `calc(${s('5xl')} + ${s('5xl')} + ${s('2xl')})`,
      }}
    >
      <Stack gap="none">
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          gap="xl"
          sx={{ px: s('xl'), pt: s('lg'), pb: s('xl') }}
        >
          <Stack gap="none" align="flex-start">
            <Text
              role="body"
              color="textSecondary"
              sx={{ mb: s('sm'), fontSize: popoverFontSize, lineHeight: 1.4 }}
            >
              From
            </Text>
            <DatePicker
              value={displayedFromDate}
              onChange={(nextValue) => {
                onFromDateChange(nextValue);
                if (nextValue !== null && toDate === null) {
                  onToDateChange(dayjs().startOf('day'));
                }
              }}
              placeholder="dd/mm/yyyy"
              lowercasePlaceholder
              maxDate={toDate ?? dayjs()}
              openPickerButtonSx={tableDatePickerOpenButtonSx(fromDate !== null)}
              textFieldSx={[
                tableDatePickerTextFieldSx,
                {
                  '& .MuiPickersInputBase-input, & .MuiPickersInputBase-sectionContent, & .MuiPickersInputBase-separator':
                    {
                      fontSize: popoverFontSize,
                      lineHeight: 1.4,
                    },
                },
                ...(shouldPreservePreviewFromDate || isQuickRangePreview
                  ? [tableDateQuickRangeTextSx]
                  : []),
              ]}
            />
          </Stack>
          <Stack gap="none" align="flex-start">
            <Text
              role="body"
              color="textSecondary"
              sx={{ mb: s('sm'), fontSize: popoverFontSize, lineHeight: 1.4 }}
            >
              To
            </Text>
            <DatePicker
              value={displayedToDate}
              onChange={(nextValue) => {
                if (!nextValue) {
                  onToDateChange(nextValue);
                  return;
                }
                if (shouldPreservePreviewFromDate || isQuickRangePreview) {
                  const nextStart = displayedFromDate ? displayedFromDate.startOf('day') : null;
                  if (nextStart !== null) {
                    onFromDateChange(nextStart);
                  }
                  const nextEnd = nextValue.startOf('day');
                  onToDateChange(nextEnd);
                  return;
                }
                onToDateChange(nextValue);
              }}
              placeholder="dd/mm/yyyy"
              lowercasePlaceholder
              minDate={displayedFromDate ?? fromDate ?? undefined}
              maxDate={dayjs()}
              openPickerButtonSx={tableDatePickerOpenButtonSx(toDate !== null)}
              textFieldSx={[
                tableDatePickerTextFieldSx,
                {
                  '& .MuiPickersInputBase-input, & .MuiPickersInputBase-sectionContent, & .MuiPickersInputBase-separator':
                    {
                      fontSize: popoverFontSize,
                      lineHeight: 1.4,
                    },
                },
                ...(shouldPreservePreviewFromDate || isQuickRangePreview
                  ? [tableDateQuickRangeTextSx]
                  : []),
              ]}
            />
          </Stack>
        </Stack>
      </Stack>
    </Popover>
  );
};
